import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, getErrorMessage } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ─── Restore session on mount ─────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('auxtax_token');
    const stored = localStorage.getItem('auxtax_user');
    if (token && stored) {
      try {
        setUser(JSON.parse(stored));
        // Verify token is still valid
        authAPI.getProfile()
          .then(res => setUser(res.data.data))
          .catch(() => {
            localStorage.removeItem('auxtax_token');
            localStorage.removeItem('auxtax_user');
            setUser(null);
          })
          .finally(() => setLoading(false));
      } catch {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const res = await authAPI.login({ email, password });
      const { token, user: userData } = res.data;
      localStorage.setItem('auxtax_token', token);
      localStorage.setItem('auxtax_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      return { success: false, error: msg };
    }
  }, []);

  const register = useCallback(async (formData) => {
    setError(null);
    try {
      const res = await authAPI.register(formData);
      const { token, user: userData } = res.data;
      localStorage.setItem('auxtax_token', token);
      localStorage.setItem('auxtax_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      return { success: false, error: msg };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auxtax_token');
    localStorage.removeItem('auxtax_user');
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedData) => {
    const merged = { ...user, ...updatedData };
    setUser(merged);
    localStorage.setItem('auxtax_user', JSON.stringify(merged));
  }, [user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateUser,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;